import { RunConfigService, ConfigService, storage } from "@nuskin/ns-util";
import axios from "axios";

const STORAGE_METADATA = storage.metadata.POPULAR_PRODUCTS;

export default {
  getPopularProducts
};

/**
 * Used to retrieve popular products from session storage or calls out to popular products lambda.
 * @param locale The locale (or countryCode for backward compatibility) of the market to get popular products
 * @return {Promise.<*>}
 */
async function getPopularProducts(locale) {
  if (!locale) {
    const runConfig = RunConfigService.getRunConfig();
    locale = `${runConfig.language}_${runConfig.country}`;
  }

  const localeInfo = getLocaleInfo(locale);
  let response = getResponseFromSession(localeInfo);
  if (!response || !response.Items) {
    response = await getPopularProductsFromService(localeInfo);
    writeResponseToSession(response, localeInfo);
  }
  return response;
}

/**
 * Retrieve popular product response from session storage
 * @returns {} - popular products
 * @private
 */
function getResponseFromSession(localeInfo) {
  initStorage();
  const stored = storage(STORAGE_METADATA);
  const response = stored[localeInfo.locale];
  return filterResponse(response, localeInfo.countryCode);
}

/**
 * Retrieve popular products from AWS lamda. This data is populated through Big Query in GTM.
 * @returns {} - popular products
 * @private
 */
async function getPopularProductsFromService(localeInfo) {
  const marketConfig = ConfigService.getMarketConfig();

  let popularProductsUrl = "";
  // remain backwards-compatible with v1, requiring only the countryCode
  if (!localeInfo.language) {
    popularProductsUrl = `${marketConfig.awsUrl}/popularproducts/v1/${localeInfo.countryCode}`;
  } else {
    popularProductsUrl = `${marketConfig.awsUrl}/popularproducts/v2/${localeInfo.locale}`;
  }

  const lambdaResponse = await axios.request({
    method: "get",
    url: popularProductsUrl,
    headers: _getHeaders(),
    responseType: "json"
  });

  const response = lambdaResponse.data;

  return filterResponse(response, localeInfo.countryCode);
}

function filterResponse(response, countryCode) {
  if (response && response.Items) {
    const popularProducts = response.Items.filter(
      (popularProduct) => popularProduct.countryCode === countryCode
    );
    const newResponse = {
      Count: popularProducts.length,
      Items: popularProducts.sort(sortByWeight),
      ScannedCount: response.ScannedCount
    };
    return newResponse;
  }
  return null;
}

/**
 * Write popular products to session storage
 * @private
 */
function writeResponseToSession(response, localeInfo) {
  initStorage();
  if (response) {
    let stored = storage(STORAGE_METADATA);
    stored[localeInfo.locale] = response;
    storage(STORAGE_METADATA, stored);
  }
}

function initStorage() {
  if (!storage(STORAGE_METADATA)) {
    storage(STORAGE_METADATA, {});
  }
}

function sortByWeight(a, b) {
  return a.weight > b.weight ? -1 : b.weight > a.weight ? 1 : 0;
}

function getLocaleInfo(locale) {
  let language = "";
  let countryCode = "";

  const splitParams = locale.split("_");
  if (splitParams.length === 1) {
    countryCode = splitParams[0];
  } else {
    language = splitParams[0];
    countryCode = splitParams[1];
  }

  return {
    countryCode: countryCode,
    language: language,
    locale: locale
  };
}

/**
 * Returns headers needed for vgService VG-Dist-Info service call
 * @returns {{Content-Type: string, client_id: string, client_secret: string}}
 * @private
 */
function _getHeaders() {
  let config = ConfigService.getMarketConfig();

  return {
    "Content-Type": "application/json",
    client_id: config.checkout.clientId,
    client_secret: config.checkout.clientSecret
  };
}
